import React, { useState, useEffect, useCallback, useRef } from 'react';
import { auth } from '../firebase';
import { t } from '@lingui/core/macro';
import { CircularProgress } from '@mui/material';
import { getApiUrl } from '../utils/api';
import { DEFAULT_COLUMNS, LoaderOnFileUpload, extractDataFromRows } from './shared';
import { useProject } from '../contexts/ProjectContext';

type FirebaseUser = { email: string | null } | null;

interface GoogleSheetsLoaderProps {
  onFileUpload: LoaderOnFileUpload;
  hasExistingData: boolean;
  onSheetsUrlUpdate?: (url: string | null) => void;
}

const GoogleSheetsLoader: React.FC<GoogleSheetsLoaderProps> = ({ onFileUpload, hasExistingData, onSheetsUrlUpdate }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [hasGoogleSheetsData, setHasGoogleSheetsData] = useState<boolean>(false);
  const [user, setUser] = useState<FirebaseUser>(null);
  const hasAutoLoadedRef = useRef<boolean>(false);
  const { selectedProject } = useProject();

  const processGoogleSheetsResult = useCallback((result: unknown): void => {
    const r = result as { success?: boolean; data?: unknown[]; sheetName?: string; spreadsheetUrl?: string } | null;
    if (!r || !r.success || !r.data) {
      throw new Error('Invalid response from server');
    }

    const jsonData: unknown[] = r.data;
    if (!Array.isArray(jsonData) || jsonData.length === 0) {
      throw new Error('No data found in Google Sheets');
    }

    const { allSheetDataRows, blocks, videoSettings, columns } = extractDataFromRows(jsonData);

    // Update the spreadsheet URL if available
    if (r.spreadsheetUrl && onSheetsUrlUpdate) {
      onSheetsUrlUpdate(r.spreadsheetUrl);
    }

    onFileUpload(
      allSheetDataRows,
      columns.length ? columns : DEFAULT_COLUMNS,
      r.sheetName || 'Sheet1',
      blocks.length > 0 ? blocks : undefined,
      videoSettings
    );
  }, [onFileUpload, onSheetsUrlUpdate]);

  const checkForGoogleSheetsData = useCallback(async (userEmail: string | null): Promise<void> => {
    console.log('Checking for Google Sheets data for user:', userEmail);
    if (hasExistingData) {
      return;
    }

    if (!userEmail) {
      setHasGoogleSheetsData(false);
      return;
    }

    try {
      setIsLoading(true);
      const response = await fetch(`${getApiUrl()}/load-google-sheets?userEmail=${encodeURIComponent(userEmail)}&projectId=${selectedProject!.id}`);
      
      if (response.ok) {
        setIsLoading(false);
        const result = await response.json();
        setHasGoogleSheetsData(true);
        setError(null);
        
        // Update the spreadsheet URL even if we don't auto-load the data
        if (result.spreadsheetUrl && onSheetsUrlUpdate) {
          onSheetsUrlUpdate(result.spreadsheetUrl);
        }
        
        if (!hasExistingData && !hasAutoLoadedRef.current) {
          try {
            processGoogleSheetsResult(result);
            hasAutoLoadedRef.current = true;
          } catch (processingError) {
            console.error('Error processing Google Sheets data during auto-load:', processingError);
          }
        }
      } else if (response.status === 404) {
        setHasGoogleSheetsData(false);
        setError(null);
        setIsLoading(false);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to check for Google Sheets data');
        setHasGoogleSheetsData(false);
        setIsLoading(false);
      }
    } catch (err) {
      console.error('Error checking for Google Sheets data:', err);
      setHasGoogleSheetsData(false);
      setError('Failed to check for Google Sheets data');
      setIsLoading(false);
    }
  }, [processGoogleSheetsResult, hasExistingData, onSheetsUrlUpdate, selectedProject]);

  const loadFromGoogleSheets = async (): Promise<void> => {
    const userEmail = user?.email;
    if (!userEmail) {
      setError('No user email available');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${getApiUrl()}/load-google-sheets?userEmail=${encodeURIComponent(userEmail)}&projectId=${selectedProject!.id}`);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to load Google Sheets data');
      }

      const result = await response.json();
      processGoogleSheetsResult(result);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load Google Sheets data';
      setError(errorMessage);
      console.error('Error loading from Google Sheets:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((firebaseUser: FirebaseUser) => {
      setUser(firebaseUser);
      if (firebaseUser) {
        checkForGoogleSheetsData(firebaseUser.email);
      } else {
        setHasGoogleSheetsData(false);
      }
    });

    return () => unsubscribe();
  }, [checkForGoogleSheetsData]);

  return (
    <div className="google-sheets-loader">
      <button 
        className="load-button"
        onClick={loadFromGoogleSheets}
        disabled={isLoading}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', width: '100%' }}>
          {isLoading ? (
            <>
              <CircularProgress size={16} style={{ marginRight: '8px' }} />
              {t`Loading from Google Sheets...`}
            </>
          ) : hasGoogleSheetsData ? (
            <>
              {/* Sync icon to indicate loading/syncing operation */}
              <span style={{ marginRight: '8px' }}>🔄</span>
              {t`Load from Google Sheets`}
            </>
          ) : (
            <>
              <span style={{ marginRight: '8px' }}>🔄</span>
              {t`No Google Sheets data available`}
            </>
          )}
        </div>
      </button>
      {error && (
        <div style={{ color: 'red', marginTop: '8px', fontSize: '14px' }}>
          {error}
        </div>
      )}
    </div>
  );
};

export default GoogleSheetsLoader;


